// components/Store/EmoticonStore.jsx

import { useEffect, useState } from 'react';
import {
    Box, Typography, Grid, Card, CardMedia, CardContent,
    Button, CircularProgress, Accordion, AccordionSummary,
    AccordionDetails, Pagination, Stack, TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';

const getFullImageUrl = (url) =>
    url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const ITEMS_PER_PAGE = 5;

const EmoticonStore = () => {
    const { userInfo, refreshUserInfo } = useUser();
    const [emoticons, setEmoticons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchEmoticons = () => {
        api.get('/api/rewards/emoticons/shop/')
            .then(res => setEmoticons(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchEmoticons();
    }, []);

    const handleBuy = async (id, price) => {
        if (userInfo.points < price) {
            alert('Leaf🌿가 부족합니다!');
            return;
        }

        try {
            await api.post(`/api/rewards/emoticons/buy/${id}/`);
            await refreshUserInfo();
            fetchEmoticons();
            alert('구매 완료!');
        } catch (err) {
            alert(err.response?.data?.detail || '구매 실패');
        }
    };

    const handlePageChange = (_, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) return <CircularProgress />;

    const filtered = emoticons.filter(e => e.name.includes(search));
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const paginatedItems = isMobile
        ? filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
        : filtered;

    return (
        <Box p={2}>
            {!isMobile && (
                <Typography variant="h5" gutterBottom>😊 이모티콘 상점</Typography>
            )}

            {isMobile && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="이모티콘 검색"
                    variant="outlined"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />
            )}

            {isMobile ? (
                <>
                    {paginatedItems.map(e => {
                        const isSoldOut = !e.is_unlimited && (e.stock ?? 0) <= 0;

                        return (
                            <Accordion key={e.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box display="flex" flexDirection="column" width="100%">
                                        <Typography fontWeight="bold">{e.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {e.price} Leaf🌿
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {e.image ? (
                                        <Box mb={1} textAlign="center">
                                            <img
                                                src={getFullImageUrl(e.image)}
                                                alt={e.name}
                                                style={{ width: '100%', height: 140, objectFit: 'contain', borderRadius: 8 }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box display="flex" alignItems="center" justifyContent="center" height={100} fontSize={36}>
                                            {e.emoji_text || '❔'}
                                        </Box>
                                    )}

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {e.is_unlimited ? '무제한' : `재고: ${e.stock ?? 0}개`}
                                    </Typography>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="medium"
                                        disabled={isSoldOut || userInfo.points < e.price}
                                        onClick={() => handleBuy(e.id, e.price)}
                                    >
                                        {isSoldOut ? '품절' : '구매'}
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}

                    {totalPages > 1 && (
                        <Stack mt={2} alignItems="center">
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Stack>
                    )}
                </>
            ) : (
                <Grid container spacing={2}>
                    {paginatedItems.map(e => {
                        const isSoldOut = !e.is_unlimited && (e.stock ?? 0) <= 0;

                        return (
                            <Grid item xs={6} sm={4} md={3} key={e.id}>
                                <Card>
                                    {e.image ? (
                                        <CardMedia
                                            component="img"
                                            image={getFullImageUrl(e.image)}
                                            alt={e.name}
                                            height="100"
                                            style={{ objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <Box display="flex" alignItems="center" justifyContent="center" height={100}>
                                            <Typography fontSize={36}>{e.emoji_text || '❔'}</Typography>
                                        </Box>
                                    )}

                                    <CardContent>
                                        <Typography align="center">{e.name}</Typography>
                                        <Typography align="center" variant="body2" color="text.secondary">
                                            {e.price} 🌿
                                        </Typography>
                                        <Typography align="center" variant="body2" color="text.secondary">
                                            {e.is_unlimited ? '무제한' : `재고: ${e.stock ?? 0}개`}
                                        </Typography>

                                        <Box mt={1} display="flex" justifyContent="center">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={isSoldOut || userInfo.points < e.price}
                                                onClick={() => handleBuy(e.id, e.price)}
                                            >
                                                {isSoldOut ? '품절' : '구매'}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default EmoticonStore;
